import { lazy, type ComponentType, type LazyExoticComponent } from 'react';

const RELOAD_MARKER_PREFIX = 'insider-performance:chunk-reload';

const CHUNK_LOAD_ERROR_PATTERNS = [
  'failed to fetch dynamically imported module',
  'error loading dynamically imported module',
  'importing a module script failed',
  'loading chunk',
  'chunkloaderror',
  'expected a javascript module script',
];

let reloadAttemptedWithoutStorage = false;

const getCurrentUrlMarker = () => {
  if (typeof window === 'undefined') {
    return RELOAD_MARKER_PREFIX;
  }

  return `${RELOAD_MARKER_PREFIX}:${window.location.pathname}${window.location.search}${window.location.hash}`;
};

const markReloadAttempted = (): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }

  const marker = getCurrentUrlMarker();

  try {
    if (window.sessionStorage.getItem(marker)) {
      return false;
    }

    window.sessionStorage.setItem(marker, 'true');
    return true;
  } catch {
    if (reloadAttemptedWithoutStorage) {
      return false;
    }

    reloadAttemptedWithoutStorage = true;
    return true;
  }
};

const clearReloadAttempt = () => {
  reloadAttemptedWithoutStorage = false;

  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.sessionStorage.removeItem(getCurrentUrlMarker());
  } catch {
    // Storage may be unavailable in private browsing or strict browser modes.
  }
};

export const isChunkLoadError = (error: unknown): boolean => {
  if (!error) {
    return false;
  }

  const message =
    error instanceof Error
      ? `${error.name} ${error.message}`
      : typeof error === 'string'
        ? error
        : String(error);

  const normalizedMessage = message.toLowerCase();
  return CHUNK_LOAD_ERROR_PATTERNS.some((pattern) => normalizedMessage.includes(pattern));
};

const reloadForFreshBuild = () => {
  if (!markReloadAttempted()) {
    return false;
  }

  window.location.reload();
  return true;
};

export function lazyWithRetry<T extends ComponentType<never>>(
  componentImport: () => Promise<{ default: T }>
): LazyExoticComponent<T> {
  return lazy(async () => {
    try {
      const component = await componentImport();
      clearReloadAttempt();
      return component;
    } catch (error) {
      if (isChunkLoadError(error) && reloadForFreshBuild()) {
        return new Promise<never>(() => undefined);
      }

      throw error;
    }
  });
}
